/**
 * CostTable Component
 *
 * Displays investment costs with schema supporting:
 * - Cost types (software, contractor, employee, etc.)
 * - Frequency (one_time, monthly, quarterly, annually)
 * - Monthly/annual calculated amounts
 * - Quantitative vs qualitative costs
 */

import { CostTypeLabel, FrequencyLabel } from '@medsuite/models';
import { ColumnDef } from '@tanstack/react-table';
import { type FC, useMemo } from 'react';

import { EditableTable, EditableTableProps } from '../EditableTable/EditableTable';
import { TableActions } from '../TableActions/TableActions';

import type { InvestmentCost } from '@medsuite/database';

export interface CostTableProps extends Omit<
  EditableTableProps<InvestmentCost>,
  'columns' | 'data'
> {
  costs: InvestmentCost[];
  onAdd?: () => void;
  onEdit?: (cost: InvestmentCost) => void;
  onDelete?: (cost: InvestmentCost) => void;
  readOnly?: boolean;
  showTotals?: boolean;
}

/**
 * Calculate monthly amount for a cost based on its frequency and type
 */
function calculateMonthlyAmount(cost: InvestmentCost): number {
  if (!cost.is_quantifiable) {
    return 0;
  }

  // Handle employee/contractor with salary or hourly
  if (cost.cost_type === 'employee' || cost.cost_type === 'contractor') {
    if (cost.annual_salary) {
      return Number(cost.annual_salary) / 12;
    }
    if (cost.hourly_rate && cost.hours_per_week) {
      return Number(cost.hourly_rate) * Number(cost.hours_per_week) * 4.33;
    }
  }

  // Handle standard amount with frequency
  const amount = Number(cost.amount) || 0;
  switch (cost.frequency) {
    case 'one_time':
      return 0; // One-time costs are not monthly recurring
    case 'weekly':
      return amount * 4.33;
    case 'monthly':
      return amount;
    case 'quarterly':
      return amount / 3;
    case 'annually':
      return amount / 12;
    default:
      return amount;
  }
}

/**
 * Format currency for display
 */
function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return '—';
  }
  return `$${value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

export const CostTable: FC<CostTableProps> = ({
  costs,
  onAdd,
  onEdit,
  onDelete,
  readOnly = false,
  showTotals = true,
  ...tableProps
}) => {
  const columns = useMemo<ColumnDef<InvestmentCost>[]>(
    () => [
      {
        accessorKey: 'cost_type',
        header: 'Type',
        size: 130,
        cell: ({ getValue, row }) => {
          const costType = getValue() as string | null;
          const isQuantifiable = row.original.is_quantifiable;

          if (costType && CostTypeLabel[costType as keyof typeof CostTypeLabel]) {
            return (
              <div className="flex flex-col">
                <span className="font-medium text-gray-900">
                  {CostTypeLabel[costType as keyof typeof CostTypeLabel]}
                </span>
                {!isQuantifiable && <span className="text-xs text-orange-600">Qualitative</span>}
              </div>
            );
          }

          return <span className="text-gray-400">—</span>;
        },
      },
      {
        accessorKey: 'cost_name',
        header: 'Name',
        size: 200,
        cell: ({ getValue }) => (
          <span className="font-medium text-gray-900">{getValue() as string}</span>
        ),
      },
      {
        accessorKey: 'cost_details',
        header: 'Details',
        size: 220,
        cell: ({ getValue }) => (
          <span className="text-gray-600 text-sm line-clamp-2">
            {(getValue() as string) || '—'}
          </span>
        ),
      },
      {
        accessorKey: 'frequency',
        header: 'Frequency',
        size: 100,
        cell: ({ getValue, row }) => {
          const frequency = getValue() as string | null;

          if (!row.original.is_quantifiable) {
            return <span className="text-gray-400">—</span>;
          }

          if (frequency && FrequencyLabel[frequency as keyof typeof FrequencyLabel]) {
            const colors: Record<string, string> = {
              one_time: 'text-blue-600 bg-blue-50',
              monthly: 'text-purple-600 bg-purple-50',
              quarterly: 'text-green-600 bg-green-50',
              annually: 'text-orange-600 bg-orange-50',
              weekly: 'text-cyan-600 bg-cyan-50',
            };
            return (
              <span
                className={`px-2 py-0.5 rounded text-xs font-medium ${colors[frequency] || 'text-gray-600 bg-gray-50'}`}
              >
                {FrequencyLabel[frequency as keyof typeof FrequencyLabel]}
              </span>
            );
          }

          return <span className="text-gray-400">—</span>;
        },
      },
      {
        accessorKey: 'amount_type',
        header: 'CapEx/OpEx',
        size: 90,
        cell: ({ getValue, row }) => {
          const amountType = getValue() as string | null;

          if (!row.original.is_quantifiable) {
            return <span className="text-gray-400">—</span>;
          }

          if (amountType === 'C') {
            return <span className="text-blue-600 font-medium">CapEx</span>;
          }
          if (amountType === 'O') {
            return <span className="text-green-600 font-medium">OpEx</span>;
          }

          return <span className="text-gray-400">—</span>;
        },
      },
      {
        id: 'monthly_amount',
        header: 'Monthly',
        size: 110,
        cell: ({ row }) => {
          if (!row.original.is_quantifiable) {
            return <span className="text-gray-400">—</span>;
          }

          const monthly = calculateMonthlyAmount(row.original);
          if (monthly === 0 && row.original.frequency === 'one_time') {
            const amount = Number(row.original.amount) || 0;
            return (
              <div className="text-right">
                <span className="text-gray-600 font-mono">{formatCurrency(amount)}</span>
                <span className="text-xs text-gray-400 block">one-time</span>
              </div>
            );
          }
          return (
            <div className="text-right font-mono text-gray-900">{formatCurrency(monthly)}</div>
          );
        },
      },
      {
        id: 'annual_amount',
        header: 'Annual',
        size: 120,
        cell: ({ row }) => {
          if (!row.original.is_quantifiable) {
            return <span className="text-gray-400">—</span>;
          }

          const monthly = calculateMonthlyAmount(row.original);
          if (row.original.frequency === 'one_time') {
            const amount = Number(row.original.amount) || 0;
            return (
              <div className="text-right font-mono font-semibold text-gray-900">
                {formatCurrency(amount)}
              </div>
            );
          }

          return (
            <div className="text-right font-mono font-semibold text-gray-900">
              {formatCurrency(monthly * 12)}
            </div>
          );
        },
      },
      {
        id: 'actions',
        header: '',
        size: 80,
        cell: ({ row }) => {
          const cost = row.original;
          return (
            <TableActions
              onEdit={onEdit ? () => onEdit(cost) : undefined}
              onDelete={onDelete ? () => onDelete(cost) : undefined}
              size="sm"
            />
          );
        },
      },
    ],
    [onEdit, onDelete]
  );

  const totals = useMemo(() => {
    if (!showTotals) {
      return null;
    }

    const quantitative = costs.filter((c) => c.is_quantifiable !== false);
    const qualitative = costs.filter((c) => c.is_quantifiable === false);

    const monthlyTotal = quantitative.reduce((sum, cost) => sum + calculateMonthlyAmount(cost), 0);

    const oneTimeTotal = quantitative
      .filter((c) => c.frequency === 'one_time')
      .reduce((sum, c) => sum + (Number(c.amount) || 0), 0);

    const capexTotal = quantitative
      .filter((c) => c.amount_type === 'C')
      .reduce((sum, c) => {
        if (c.frequency === 'one_time') {
          return sum + (Number(c.amount) || 0);
        }
        return sum + calculateMonthlyAmount(c) * 12;
      }, 0);

    const opexTotal = quantitative
      .filter((c) => c.amount_type === 'O')
      .reduce((sum, c) => {
        if (c.frequency === 'one_time') {
          return sum + (Number(c.amount) || 0);
        }
        return sum + calculateMonthlyAmount(c) * 12;
      }, 0);

    return {
      quantitativeCount: quantitative.length,
      qualitativeCount: qualitative.length,
      monthlyTotal,
      annualTotal: monthlyTotal * 12 + oneTimeTotal,
      capexTotal,
      opexTotal,
    };
  }, [costs, showTotals]);

  return (
    <div className="space-y-3">
      {onAdd && !readOnly && (
        <div className="flex justify-end">
          <button
            onClick={onAdd}
            className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            Add Cost
          </button>
        </div>
      )}
      <EditableTable
        columns={columns}
        data={costs}
        readOnly={readOnly}
        emptyMessage="No costs added yet"
        compact
        {...tableProps}
      />
      {showTotals && totals && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
          <div className="grid grid-cols-4 gap-4">
            <div>
              <div className="text-xs font-medium uppercase tracking-wider text-gray-500">
                Costs
              </div>
              <div className="mt-1 text-xl font-bold text-gray-900">
                {totals.quantitativeCount} quantitative
              </div>
              {totals.qualitativeCount > 0 && (
                <div className="text-sm text-orange-600">{totals.qualitativeCount} qualitative</div>
              )}
            </div>
            <div>
              <div className="text-xs font-medium uppercase tracking-wider text-gray-500">
                Monthly Total
              </div>
              <div className="mt-1 text-xl font-bold text-gray-900">
                {formatCurrency(totals.monthlyTotal)}
              </div>
            </div>
            <div>
              <div className="text-xs font-medium uppercase tracking-wider text-gray-500">
                Annual Total
              </div>
              <div className="mt-1 text-xl font-bold text-red-600">
                {formatCurrency(totals.annualTotal)}
              </div>
            </div>
            <div className="border-l pl-4">
              <div className="text-xs font-medium uppercase tracking-wider text-gray-500">
                By Type
              </div>
              <div className="mt-1 space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-blue-600">CapEx:</span>
                  <span className="font-mono">{formatCurrency(totals.capexTotal)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-green-600">OpEx:</span>
                  <span className="font-mono">{formatCurrency(totals.opexTotal)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
